import type { MouseEvent } from 'react';
import { ChangeEvent, FunctionComponent, KeyboardEvent, useState } from 'react';
import { Box, InputAdornment, Link, Typography } from '@material-ui/core';
import { AccountCircle, Lock } from '@material-ui/icons';
import { updateFormData } from 'utils/form';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import signInStyles from 'pages/signin/styles';
import validateForm from 'pages/signin/utils';
import LoadingButton from 'components/LoadingButton';
import i18n from 'i18n';
import TextField from 'components/TextField';
import { useNavigate } from 'react-router';

interface SignInFormI {
  onClick: (username: string, password: string) => void;
  isLoading: boolean;
}

const SignInForm: FunctionComponent<SignInFormI> = ({
  onClick,
  isLoading
}: SignInFormI) => {
  const [formData, setFormData] = useState<{
    username: string;
    password: string;
    errors: { [key: string]: boolean };
  }>({
    username: '',
    password: '',
    errors: { username: false, password: false }
  });

  const navigate = useNavigate();

  const onChangeField = (e: ChangeEvent<HTMLInputElement>) => {
    const updatedFormData = updateFormData(e, formData, validateForm);
    setFormData(updatedFormData);
  };

  const onKeyPress = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter') {
      onClick(formData.username, formData.password);
    }
  };

  const classes = signInStyles();

  const onResetPassword = (e: MouseEvent) => {
    e.preventDefault();
    navigate('/password-reset');
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      gridGap="16px"
      onKeyPress={onKeyPress}
    >
      <TextField
        variant="outlined"
        margin="normal"
        fullWidth
        id="username"
        label={i18n.t('pages.signin.formLabel.username')}
        helperText={formData.errors.username}
        name="username"
        onChange={onChangeField}
        error={!!formData?.errors?.username}
        autoComplete="username"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <AccountCircle color="primary" />
            </InputAdornment>
          )
        }}
        autoFocus
      />
      <TextField
        variant="outlined"
        margin="normal"
        fullWidth
        name="password"
        label={i18n.t('pages.signin.formLabel.password')}
        type="password"
        id="password"
        helperText={formData.errors.password}
        error={!!formData?.errors?.password}
        onChange={onChangeField}
        autoComplete="current-password"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Lock color="primary" />
            </InputAdornment>
          )
        }}
      />
      <div className={classes.actionsWrapper}>
        <FormControlLabel
          classes={{ label: classes.checkboxLabel }}
          control={
            <Checkbox
              className={classes.checkboxIcon}
              value="remember"
              color="secondary"
            />
          }
          label={i18n.t('pages.signin.formLabel.rememberMe')}
        />
        <Link href="/" onClick={onResetPassword}>
          <Typography className={classes.forgotPassword}>
            {i18n.t('pages.signin.forgotPassword')}
          </Typography>
        </Link>
      </div>
      <Box justifyContent="stretch" display="flex">
        <LoadingButton
          isLoading={isLoading}
          type="submit"
          label={i18n.t('button.signin')}
          fullWidth
          variant="contained"
          color="primary"
          onClick={() => onClick(formData.username, formData.password)}
        />
      </Box>
    </Box>
  );
};

export default SignInForm;
